import { createContext, useContext, useState } from 'react'
import type { Me } from '../types/auth'
import { shellRoleOf, type ShellRole } from './permissions'
import { storeViewAs, storedViewAs } from './navigation'

// The role the shell renders for: the real one, or a consultant's "View as" preview
// for the current client. Shared by the sidebar (menu) and the shell (preview banner).
export function useViewAs(me: Me | null | undefined, orgId: string | null) {
  const realRole = shellRoleOf(me)
  const canPreview = realRole === 'consultant'
  const [preview, setPreview] = useState<{ orgId: string | null; role: ShellRole | null }>(() => ({ orgId, role: storedViewAs(orgId) }))
  const previewRole = preview.orgId === orgId ? preview.role : storedViewAs(orgId)
  const role: ShellRole = canPreview && orgId ? (previewRole ?? realRole) : realRole
  const setRole = (v: ShellRole) => { setPreview({ orgId, role: v }); storeViewAs(orgId, v) }
  return { role, realRole, canPreview, previewing: role !== realRole, setRole }
}

export type ViewAsState = ReturnType<typeof useViewAs>

export const ViewAsContext = createContext<ViewAsState | null>(null)

// True while a consultant previews a client role; pages with a client version switch to it.
export function useClientPreview(): boolean {
  const viewAs = useContext(ViewAsContext)
  return !!viewAs && viewAs.role !== 'consultant'
}
